<template>
  <SearchInput
      v-if="participantsItems.length"
      v-model="participantsSearchValue"
      class="util--ml-auto"/>
  <DataTable
      :headers="participantsHeader"
      :items="participantsItems"
      :search-value="participantsSearchValue"
      @delete="handleDelete"/>
</template>

<script lang="ts" setup>
import DataTable from "@/components/layout/DataTable.vue";
import SearchInput from "@/components/layout/SearchInput.vue";
import {computed, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import {ActivityRegistration, Participant} from "@/types/entities/activity";
import {Guid} from "@/types";

const {t} = useI18n();

// eslint-disable-next-line
interface propsInterface {
  participants: Participant[],
  activityRegistration: ActivityRegistration | undefined,
  isLessThanTwoDays: boolean
}

// eslint-disable-next-line no-undef
const props = defineProps<propsInterface>();

// eslint-disable-next-line
const emit = defineEmits<{
  (event: "deleteParticipant", participantId: string): void;
}>();

const participants = ref(props.participants);
const isLessThanTwoDays = ref(props.isLessThanTwoDays);
const activityRegistration = ref(props.activityRegistration);
const participantsSearchValue = ref("")

watch(props, (newProps) => {
  participants.value = newProps.participants;
  isLessThanTwoDays.value = newProps.isLessThanTwoDays;
  activityRegistration.value = newProps.activityRegistration;
});

async function handleDelete(participant: any) {
  if (participant?.id == null || !Guid.isValid(participant.id ?? ""))
    return;

  let confirmDelete = confirm(t("participants.form.deleteConfirmation"));
  if (!confirmDelete)
    return;

  emit("deleteParticipant", participant.id)
}

// DataTable
const participantsHeader = computed(() => {
  let headers = [
    {
      text: t("participants.lastName"),
      value: "lastName",
      sortable: true
    },
    {
      text: t("participants.firstName"),
      value: "firstName",
      sortable: true
    },
    {
      text: t("participants.email"),
      value: "emailAddress",
      sortable: true
    },
    {
      text: t("participants.allergies"),
      value: "allergies",
      sortable: true
    },
    {
      text: t("participants.reducedMobility"),
      value: "reducedMobility",
      sortable: true
    }
  ];

  if (!isLessThanTwoDays.value) {
    headers.push({
      text: t("global.table.actions"),
      value: "actions",
      sortable: false
    })
  }
  return headers;
})

const participantsItems = computed(() => {
  let items: any[] = [];
  if (participants.value != null && participants.value.length > 0) {
    participants.value.forEach((participant: Participant) => {
      items.push({
        ...participant,
        allergies: participant.allergies?.length ? participant.allergies : t('global.undefined'),
        reducedMobility: participant.reducedMobility ? t('global.yes') : t('global.no'),
        actions: {
          delete: !isLessThanTwoDays.value
        },
      });
    });
  }

  return items
})
</script>